<template>
  <b-card class="sales-stage-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body>
    <template #header>
      <h6 class="mb-0">Sales by Stage</h6>
    </template>
    <b-card-body>
      <apexchart type="bar" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'SalesStageChart',
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: [], // Stage names will go here
        },
        yaxis: {
          title: {
            text: 'Count',
          },
        },
      },
      series: [{
        name: 'Sales',
        data: [],
      }],
    };
  },
  computed: {
    safeSeries() {
      const s = this.series || [];
      const mapped = s.map((item) => ({ name: item.name || '', data: Array.isArray(item.data) ? item.data : [] }));
      return mapped.length ? mapped : [{ name: 'Sales', data: [] }];
    },
  },
  watch: {
    dashboardData: {
      deep: true,
      handler(val) {
        if (val && val.sales_stage_chart != null && hasRight(this.user, 'dashboard', 'view')) {
          this.processChartData(Array.isArray(val.sales_stage_chart) ? val.sales_stage_chart : []);
        }
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.sales_stage_chart != null) this.processChartData(Array.isArray(this.dashboardData.sales_stage_chart) ? this.dashboardData.sales_stage_chart : []);
          return;
        }
        if (this.dashboardData && this.dashboardData.sales_stage_chart != null) { this.processChartData(Array.isArray(this.dashboardData.sales_stage_chart) ? this.dashboardData.sales_stage_chart : []); return; }
        this.fetchSalesStageChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.sales_stage_chart != null) this.processChartData(Array.isArray(this.dashboardData.sales_stage_chart) ? this.dashboardData.sales_stage_chart : []);
            return;
          }
          this.fetchSalesStageChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [{ name: 'Sales', data: [] }];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchSalesStageChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.sales_stage_chart != null)) {
        if (this.dashboardData && this.dashboardData.sales_stage_chart != null) this.processChartData(Array.isArray(this.dashboardData.sales_stage_chart) ? this.dashboardData.sales_stage_chart : []);
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/sales-stage-chart', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching sales stage chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || !Array.isArray(data)) {
        this.series = [{ name: 'Sales', data: [] }];
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        return;
      }
      const categories = data.map(item => item.stage_name);
      const seriesData = data.map(item => item.count);

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories: categories,
        },
      };

      this.series = [{
        name: 'Sales',
        data: seriesData,
      }];
    },
  },
};
</script>

<style scoped>
.sales-stage-chart {
  margin-top: 20px;
}
</style>